/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useMemo, useState } from 'react';
import { dehydrate } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import queryString from 'query-string';

import { ProviderDetails, ServicesOffered, GlobalContentLayout } from '@/components';
import { PageProvider } from '@/context/PageContext';
import useServicesState from '@/hooks/useServicesState';
import { useShowComponent } from '@/hooks/showComponent';
import { useIframeRouter } from '@/hooks/useIframeRouter';

import * as St from './ServiceProvider.styles';
import { ServiceProviderPageProps } from './ServiceProvider.types';
import { BranchOfficeHeader } from '../BranchOffice/components/BranchOfficeHeader';
import { withMinisiteOverview } from '@/entries/_shared/minisiteOverviewGssp';
import { getServiceProviders } from '@/services/serviceProviders/serviceProviders.repo';
import { useServiceProviders } from '@/services/serviceProviders';
import { getLocation } from '@/services/locations/locations.repo';
import { ServiceModel, useServices } from '@/services/services';
import usePageContext from '@/hooks/usePageContext';

const ServiceProviderContent: FC = () => {
  const router = useRouter();
  const { countryCode, locationId, providerId } = router.query as {
    countryCode: string;
    locationId: string;
    providerId: string;
  };
  const { queryParams, setSelectedServices } = usePageContext();
  const { routerPush } = useIframeRouter();
  const { data: providers, isLoading: isLoadingProviders } = useServiceProviders(locationId);
  const { data: servicesResponse, isLoading: isLoadingServices } = useServices(locationId, undefined);

  const provider = providers?.find((currentProvider) => currentProvider.id === Number(providerId));

  const filteredServices = useMemo(
    () =>
      (servicesResponse?.services || [])
        .map((category) => ({
          ...category,
          services: category.services.filter(
            (service) =>
              !service.bundled &&
              service.serviceProviders.some((serviceProvider) => serviceProvider.id === Number(providerId)),
          ),
        }))
        .filter((category) => category.services.length > 0),
    [servicesResponse, providerId],
  );

  const isLoading = isLoadingProviders || isLoadingServices;
  const { state, dispatch } = useServicesState(isLoading ? undefined : filteredServices);
  const { hide: hideCart, show: showCart, isShown: isCartShown } = useShowComponent();
  const [_, setWiggle] = useState(false);
  const [selected, setSelected] = useState<Array<ServiceModel>>([]);

  useEffect(() => {
    if (setSelectedServices) {
      setSelectedServices(!!selected.length);
    }
  }, [selected]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!provider || !filteredServices.length) {
      routerPush(`/${countryCode}/sucursal/${locationId}/${queryParams}`);
    }
  }, [isLoading, provider, filteredServices]);

  return (
    <GlobalContentLayout>
      <St.Wrapper>
        {provider && (
          <ProviderDetails
            name={provider.publicName}
            schedule={provider.providerTimes}
            imageUrl={provider.image}
            biography={provider.biography || ''}
          />
        )}
        <ServicesOffered
          services={state}
          dispatch={dispatch}
          serviceProvider={provider}
          isLoading={isLoading}
          selected={selected}
          setSelected={(items: ServiceModel[]) => setSelected(items)}
          setWiggle={setWiggle}
          isCartShown={isCartShown}
          hideCart={hideCart}
          showCart={showCart}
        />
      </St.Wrapper>
    </GlobalContentLayout>
  );
};

const ServiceProvider: FC<ServiceProviderPageProps> = ({
  locationId,
  provider,
  companyOverview,
  currentLocation,
  companySlug,
  queryParams,
}) => {
  const { t } = useTranslation();

  return (
    <PageProvider
      companySlug={companySlug}
      companyOverview={companyOverview}
      locationId={locationId}
      nextSeo={
        provider
          ? {
              title: `${provider.publicName} ${t('SEO.IN')} ${companyOverview.name} | ${t('SEO.BOOK_ONLINE')}`,
              description: `${t('SEO.PROVIDERS_DESCRIPTION')} ${provider.publicName} ${t('SEO.IN')} ${
                companyOverview.name
              } - ${currentLocation.locationName}`,
            }
          : undefined
      }
      queryParams={queryParams}
    >
      <BranchOfficeHeader />
      <ServiceProviderContent />
    </PageProvider>
  );
};

export default ServiceProvider;

export const getServerSideProps = withMinisiteOverview(async (ctx, { companyOverview, companySlug, queryClient }) => {
  const { countryCode, locationId, providerId, token, ...params } = ctx.query as {
    providerId: string;
    countryCode: string;
    locationId: string;
    token: string | undefined;
  };

  const query = queryString.stringify(params);
  const queryParams = query ? `?${query}` : '';

  if (companyOverview.showProviders === 2) {
    return {
      redirect: {
        permanent: false,
        destination: `/${countryCode}/sucursal/${locationId}/${queryParams}`,
      },
    };
  }

  const currentLocation = await queryClient.fetchQuery(['getLocation', locationId], ({ queryKey }) =>
    getLocation(parseInt(queryKey[1], 10)),
  );

  const providers = await queryClient.fetchQuery({
    queryKey: ['getServiceProviders', locationId, companyOverview.id],
    queryFn: () => getServiceProviders(locationId, companyOverview.id),
  });

  const provider = providers.find((currentProvider) => currentProvider.id === Number(providerId)) || null;

  if (!provider) {
    return {
      redirect: {
        permanent: false,
        destination: `/${countryCode}/sucursal/${locationId}/${queryParams}`,
      },
    };
  }

  return {
    props: {
      companySlug,
      dehydratedState: dehydrate(queryClient),
      locationId,
      providerId,
      currentLocation,
      companyOverview,
      provider,
      queryParams,
    },
  };
});
